import { Component, Emit, Inject, Prop, Vue } from 'vue-property-decorator'
import { Nullable, StrictNullable } from '@/shared/types/app.types'
import {
    InvoicePaymentPatchRequest,
    InvoicePaymentResponse,
    InvoicePaymentStatus
} from '@/api/services/billing/invoice-payment/billing-invoice-payment.dto'
import { billingInvoicePaymentService } from '@/api/services/billing/invoice-payment/billing-invoice-payment.service'
import { InvoiceResponse } from '@/api/services/billing/common-invoice/billing-common-invoice.dto'
import { AppliedBillableConceptResponse } from '@/api/services/billing/applied-billable-concept/billing-applied-billable-concept.dto'
import { Validator } from 'vee-validate'
import { PromiseUtils } from '@/shared/util/promise-utils'
import { ToastUtils } from '@/shared/util/toast-utils'
import { store } from '@/store/store'
import { HelperUtils } from '@/shared/util/helper-utils'
import { BillingOperation } from '@/api/operations/models/billing-operation'
import { DateUtils } from '@/shared/util/date-utils'
import { BillingOperationWithConceptsGuard } from '@/modules/billing-module/shared/guards/billing-operation-with-concepts-guard'

@Component
export default class BillingModalInvoicePaymentActionsModal extends Vue {
    @Prop({ type: Object }) readonly invoicePayment!: StrictNullable<InvoicePaymentResponse>
    @Prop({ type: Object }) readonly invoice!: Nullable<InvoiceResponse>
    @Prop({ type: Boolean, default: true }) readonly selectionMode!: boolean
    @Prop({ type: Number, default: 0 }) readonly invoiceAppliedBillableConceptsCount!: number
    @Prop({ type: Array, default: () => [] }) readonly appliedBillableConcepts!: AppliedBillableConceptResponse[]

    @Inject() externalValidator!: Validator

    constructor() {
        super()
        this.$validator = this.externalValidator
    }

    @Emit()
    refresh(invoicePaymentId: number): number {
        return invoicePaymentId
    }

    private get _invoicePaymentGenerated(): boolean {
        return this.invoicePayment?.status.code === InvoicePaymentStatus.GENERATED
    }
    private get _invoicePaymentCancelled(): boolean {
        return this.invoicePayment?.status.code === InvoicePaymentStatus.CANCELLED
    }
    private get _invoicePaymentVerified(): boolean {
        return this.invoicePayment?.status.code === InvoicePaymentStatus.VERIFIED
    }

    private get appliedBillableConceptsIds(): number[] {
        return this.appliedBillableConcepts.map(abc => abc.id)
    }

    get disabledOnAddAllButton(): boolean {
        return !this.invoiceAppliedBillableConceptsCount
    }

    get visibleAdd(): boolean {
        return this.selectionMode &&
            (!this.invoicePayment || this._invoicePaymentGenerated)
    }
    get canAddSelected(): boolean {
        return this.appliedBillableConceptsIds.length > 0
    }

    get visibleUpdateInvoicePayment(): boolean {
        return !this.selectionMode &&
            (this._invoicePaymentVerified || this._invoicePaymentCancelled)
    }

    get visibleCancelInvoicePayment(): boolean {
        return !this.selectionMode &&
            this._invoicePaymentGenerated
    }
    get visibleVerifyInvoicePayment(): boolean {
        return !this.selectionMode &&
            this._invoicePaymentGenerated &&
            this.invoiceAppliedBillableConceptsCount > 0
    }
    get visibleReplaceReport(): boolean {
        return this._invoicePaymentVerified && HelperUtils.hasOperationInCurrentLaboratory(BillingOperation.REPLACE_INVOICE_PAYMENTS_REPORTS)
    }

    private async _createInvoicePaymentFromInvoice(): Promise<void> {
        if (this.invoice) {
            const response =
                await PromiseUtils.toCallSecure(
                    billingInvoicePaymentService.createFromInvoice(
                        store.state.labId,
                        this.invoice.id))
            if (response !== null) {
                ToastUtils.showToastSuccess(this.$tc('accounting.invoicePayment.generated'))
                this.refresh(response.id) // emits an event
            }
        }
    }
    private async _createInvoicePaymentFromAppliedBillableConcepts(): Promise<void> {
        if (this.appliedBillableConceptsIds.length) {
            const response =
                await PromiseUtils.toCallSecure(
                    billingInvoicePaymentService.createFromAppliedBillableConcepts(
                        store.state.labId,
                        this.appliedBillableConceptsIds))
            if (response !== null) {
                ToastUtils.showToastSuccess(this.$tc('accounting.invoicePayment.generated'))
                this.refresh(response.id) // emits an event
            }
        }
    }

    private async _addAllSelectedAppliedBillableConceptsToInvoicePayment(): Promise<void> {
        if (this.invoicePayment) {
            const response =
                await PromiseUtils.toCallSecure(
                    billingInvoicePaymentService.addAllConcepts(
                        store.state.labId,
                        this.invoicePayment.id))
            if (response !== null) {
                ToastUtils.showToastSuccess(this.$tc('accounting.concept.added'))
                this.refresh(this.invoicePayment.id) // emits an event
            }
        }
    }
    private async _addSelectedAppliedBillableConceptsToInvoicePayment(): Promise<void> {
        if (this.invoicePayment && this.appliedBillableConceptsIds.length) {
            const response =
                await PromiseUtils.toCallSecure(
                    billingInvoicePaymentService.addConcepts(
                        store.state.labId,
                        this.invoicePayment.id,
                        this.appliedBillableConceptsIds))
            if (response !== null) {
                ToastUtils.showToastSuccess(this.$tc('accounting.concept.added'))
                this.refresh(this.invoicePayment.id) // emits an event
            }
        }
    }

    async onAddAll(): Promise<void> {
        if (this.invoicePayment) {
            const { invoice, appliedBillableConceptCount } = this.invoicePayment
            const numberOfConcepts = invoice.appliedBillableConceptsCount - appliedBillableConceptCount
            await BillingOperationWithConceptsGuard.run(
                numberOfConcepts,
                this._addAllSelectedAppliedBillableConceptsToInvoicePayment
            )
        } else if (this.invoice) {
            await BillingOperationWithConceptsGuard.run(
                this.invoice.appliedBillableConceptsCount,
                this._createInvoicePaymentFromInvoice
            )
        }
    }
    async onAddSelected(): Promise<void> {
        if (this.invoicePayment) {
            await BillingOperationWithConceptsGuard.run(
                this.appliedBillableConceptsIds.length,
                this._addSelectedAppliedBillableConceptsToInvoicePayment
            )
        } else {
            await BillingOperationWithConceptsGuard.run(
                this.appliedBillableConceptsIds.length,
                this._createInvoicePaymentFromAppliedBillableConcepts
            )
        }
    }

    private async _updateInvoicePayment(): Promise<boolean> {
        const valid = await this.$validator.validateAll()
        if (valid && this.invoicePayment) {
            const { id, externalReference, sentToGovernmentRevenueServices, paymentDate, paymentMethod } = this.invoicePayment
            const request: InvoicePaymentPatchRequest = {
                id,
                externalReference,
                sentToGovernmentRevenueServices,
                paymentDate: paymentDate ? DateUtils.formatDate(new Date(paymentDate), DateUtils.datePattern) : undefined,
                paymentMethodId: paymentMethod?.id
            }
            const response =
                await PromiseUtils.toCallSecure(
                    billingInvoicePaymentService.patch(store.state.labId, request))
            return response !== null
        }
        return false
    }
    async onUpdateInvoicePayment(): Promise<void> {
        if (this.invoicePayment && await this._updateInvoicePayment()) {
            ToastUtils.showToastSuccess(this.$tc('billing.modal.invoicePayment.message.updated'))
            this.refresh(this.invoicePayment.id) // emits an event
        }
    }

    async onCancelInvoicePayment(): Promise<void> {
        if (this.invoicePayment) {
            const { id, appliedBillableConceptCount } = this.invoicePayment
            await BillingOperationWithConceptsGuard.run(
                appliedBillableConceptCount,
                async () => {
                    const response =
                        await PromiseUtils.toCallSecure(
                            billingInvoicePaymentService.cancel(store.state.labId, id))
                    if (response !== null) {
                        ToastUtils.showToastSuccess(this.$tc('billing.modal.invoicePayment.message.cancelled'))
                        this.refresh(id) // emits an event
                    }
                }
            )
        }
    }
    async onVerifyInvoicePayment(): Promise<void> {
        if (this.invoicePayment) {
            const { id, appliedBillableConceptCount } = this.invoicePayment
            await BillingOperationWithConceptsGuard.run(
                appliedBillableConceptCount,
                async () => {
                    if (!this.visibleUpdateInvoicePayment || await this._updateInvoicePayment()) {
                        const response =
                        await PromiseUtils.toCallSecure(
                            billingInvoicePaymentService.verify(store.state.labId, id))
                        if (response !== null) {
                            ToastUtils.showToastSuccess(this.$tc('billing.modal.invoicePayment.message.verified'))
                            this.refresh(id) // emits an event
                        }
                    }
                }
            )
        }
    }
    async onReplaceReport(): Promise<void> {
        if (this.invoicePayment) {
            const response =
                await PromiseUtils.toCallSecure(
                    billingInvoicePaymentService.replaceReport(store.state.labId, this.invoicePayment.id))
            if (response !== null) {
                ToastUtils.showToastSuccess(this.$tc('billing.modal.invoicePayment.message.invoicePaymentReportReplaced'))
                this.refresh(this.invoicePayment.id) // emits an event
            }
        }
    }
}
